import type { JSX } from 'react';
import { cn } from '@/lib/utils';
import { fieldLabel, input } from '../shell/look';

const STEPS = ['Account', 'Login code', 'Two-factor'] as const;

/** Three bars, filled up to the step reached — where you are, and how far is left. */
export function StepBars({ reached }: { reached: number }): JSX.Element {
  return (
    <div className="flex gap-1.5">
      {STEPS.map((label, i) => (
        <div key={label} className="flex-1">
          <div className={cn('h-1 rounded-sm', i + 1 <= reached ? 'bg-acc' : 'bg-surf2')} />
          <div
            className={cn(
              'mt-[7px] font-mono text-[10.5px] tracking-[.06em]',
              i + 1 <= reached ? 'text-ink' : 'text-mute',
            )}
          >
            {i + 1}. {label.toUpperCase()}
          </div>
        </div>
      ))}
    </div>
  );
}

/** docs/telegram.md: Telegram cancels a login code that appears inside a Telegram chat. */
export function CodeWarning(): JSX.Element {
  return (
    <div className="border-warn text-mute rounded-[10px] border bg-[color-mix(in_oklch,var(--warn)_8%,var(--surf))] px-3.5 py-3 text-[12.5px] leading-[1.55]">
      <div className="text-warn mb-0.5 font-medium">Never paste the code into a Telegram chat</div>
      Telegram cancels a login code as soon as it appears in a chat. If a code stops working, run{' '}
      <span className="text-ink font-mono text-xs">pnpm telegram:login</span> and paste the session
      string instead.
    </div>
  );
}

export function LoginRow(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  help?: string;
  mono?: boolean;
  secret?: boolean;
}): JSX.Element {
  return (
    <label htmlFor={props.id} className={fieldLabel}>
      {props.label}
      <input
        id={props.id}
        value={props.value}
        type={props.secret === true ? 'password' : 'text'}
        autoComplete="off"
        className={cn(input, props.mono === true && 'font-mono text-[13.5px]')}
        onChange={(event) => props.onChange(event.target.value)}
      />
      {props.help !== undefined && <span className="text-mute text-xs">{props.help}</span>}
    </label>
  );
}

/**
 * What Telegram's answer means, in words. The API passes Telegram's own token
 * through (API_ID_INVALID, FLOOD_WAIT_300…); the documented ones get a sentence,
 * and anything else is shown as Telegram said it rather than guessed at.
 */
export function loginMessage(status: number, body: unknown): string {
  const token = (body as { message?: unknown } | null)?.message;
  const code = typeof token === 'string' ? token : '';

  if (status === 400) return 'Fill in every field: name, API ID, API hash and phone.';
  if (status === 403) return 'Only an admin can connect an account.';
  if (status === 410) return 'That login expired. Start again.';
  if (status === 503) return 'SECRETS_KEY is not configured, so no session can be stored.';

  const wait = /^FLOOD_WAIT_(\d+)$/.exec(code);
  if (wait?.[1] !== undefined) {
    return `Telegram asks to wait ${Math.ceil(Number(wait[1]) / 60)} min before trying again.`;
  }
  const known: Record<string, string> = {
    API_ID_INVALID:
      'The API ID and API hash do not belong to an app. Copy both again from my.telegram.org → API development tools.',
    PHONE_NUMBER_INVALID: 'Telegram does not recognise that phone number.',
    PHONE_NUMBER_BANNED: 'The account for this number is banned by Telegram.',
    PHONE_CODE_INVALID: 'That code is wrong. Check it and try again.',
    PHONE_CODE_EXPIRED:
      'That code expired — or it was shown inside a Telegram chat, which cancels it. Start again, or use pnpm telegram:login.',
  };
  if (known[code] !== undefined) return known[code];
  if (code !== '' && code !== 'Telegram could not be reached') return `Telegram said: ${code}`;
  return 'Telegram could not be reached.';
}

export function ctaLabel(step: 1 | 2 | 3, pending: boolean): string {
  if (pending) return 'Verifying…';
  if (step === 1) return 'Send login code';
  if (step === 2) return 'Confirm code';
  return 'Connect & verify';
}
